import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { Oval } from "react-loader-spinner";
import Input from "components/input";
import Select from "components/selectWithComponents";
import { ReactComponent as AddCircleIcon } from "images/auth/add-circle.svg";
import { ReactComponent as TrashIcon } from "images/auth/trash.svg";
import { useLookupsContext } from "hooks/useContext";
import { domainOtherOption, catToolsOptions } from "utils/constantValues";
import RadioButtons from "components/radioButtons";
import CreatableMultiSelect from "components/creatableMultiSelect";
import { ReactComponent as LockIcon } from "images/profile/lock.svg";

function EditExperience({
  onCancel = () => {},
  onSave = () => {},
  isLoading = false,
}) {
  const [t] = useTranslation();
  const userDetails = useSelector((state) => state.userDetails || {});
  const userDomains = useSelector((state) => state.userDomains || []);
  const userSubDomains = useSelector((state) => state.userSubDomains || []);
  const [radios, setRadios] = useState([
    {
      title: t("Yes"),
      isActive: userDetails?.isWillingToTravel,
    },
    { title: t("No"), isActive: !userDetails?.isWillingToTravel },
  ]);
  const [secondmentRadios, setSecondmentRadios] = useState([
    {
      title: t("Yes"),
      isActive: userDetails?.isOpenForSecondment ?? false,
    },
    {
      title: t("No"),
      isActive: !(userDetails?.isOpenForSecondment ?? false),
    },
  ]);

  const [catToolsRadios, setCatToolsRadios] = useState([
    { title: t("Yes"), isActive: userDetails?.hasCatToolsExperience ?? false },
    { title: t("No"), isActive: !(userDetails?.hasCatToolsExperience ?? false) },
  ]);
  const {
    domains = [],
    subDomains = [],
    getSubDomains,
  } = useLookupsContext(false);

  const domainsOptions = domains.map((domain) => ({
    ...domain,
    value: domain.id,
    label: domain.title,
  }));

  const subDomainsOptions = subDomains.map((el) => ({
    ...el,
    label: el.title,
    value: el.id,
  }));

  const defaultValues = {
    userDomains: userDomains.reduce((acc, domain) => {
      const domainSubDomains = userSubDomains
        .filter((subDomain) => subDomain.domainId === domain.id)
        .map((sub) => sub.id);
      acc[domain.id] = domainSubDomains;
      return acc;
    }, {}),
    yearOfExperience: userDetails?.yearOfExperience ?? "",
    isWillingToTravel: userDetails?.isWillingToTravel ?? true,
    isOpenForSecondment: userDetails?.isOpenForSecondment ?? false,
    hasCatToolsExperience: userDetails?.hasCatToolsExperience ?? false,
    catTools: userDetails?.catTools ?? [],
  };

  const [userChosenDomains, setUserChosenDomains] = useState({}); // domainId:[subDomainId]

  const [domainsSection, setDomainSection] = useState(() => {
    if (userDomains.length > 0) {
      return userDomains.map(
        (userDomain) =>
          domains.find((d) => d.id === userDomain.id) || userDomain
      );
    }
    return domains[0] ? [domains[0]] : [1];
  });

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    unregister,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: defaultValues,
  });

  const addAnotherDomain = (index) => {
    if (!domainsOptions[index]) {
      return;
    }
    setDomainSection((prevState) => [...prevState, domainsOptions[index]]);
  };

  useEffect(() => {
    if (userDomains.length > 0) {
      userDomains.forEach((domain, index) => {
        setUserChosenDomains((prevState) => ({
          ...prevState,
          [domain.id]: userSubDomains
            .filter((sub) => sub.domainId === domain.id)
            .map((sub) => sub.id),
        }));

        setValue(`domainId-${index}`, domain.id);
        const subDomainIds = userSubDomains
          .filter((sub) => sub.domainId === domain.id)
          .map((sub) => sub.id);
        if (subDomainIds.length > 0) {
          setValue(`subDomains-${index}`, subDomainIds);
        }

        // Handle custom domain if it's the "Others" option
        if (domain.title === domainOtherOption && domain.customDomain) {
          setValue(`customDomain`, domain.customDomain);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const domainIds = Object.keys(userChosenDomains);

    if (domainIds.length > 0) {
      getSubDomains(domainIds);
    }
  }, [userChosenDomains]);

  const onSubmit = (values) => {
    const hasCatTools = catToolsRadios[0].isActive;
    onSave({
      domains: Object.keys(userChosenDomains),
      subDomains: Object.keys(userChosenDomains).reduce((acc, domainId) => {
        return (acc = [...acc, ...userChosenDomains[domainId]]);
      }, []),
      customDomain: values.customDomain,
      isWillingToTravel:
        radios.find((elem) => elem.isActive)?.title === t("Yes"),
      isOpenForSecondment:
        secondmentRadios.find((elem) => elem.isActive)?.title === t("Yes"),
      yearOfExperience: values.yearOfExperience,
      hasCatToolsExperience: hasCatTools,
      catTools: hasCatTools ? (values.catTools ?? []) : [],
    });
  };

  return (
    <form
      className="flex flex-col self-stretch rounded-[16px] border-[1px] border-solid border-primary"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="flex p-[24px] justify-between align-center self-stretch border-b border-solid border-[#E4E4E7]">
        <div className="text-[#1D1B22] font-semibold text-[24px]">
          {t("experience")}
        </div>

        <div className="flex items-center gap-[12px]">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="cursor-pointer flex py-[14px] px-[16px] gap-[8px] justify-center items-center rounded-[6px] border-[1px] border-solid border-default-color bg-white h-[46px]"
          >
            <div className="text-base-black font-bold text-[16px] not-italic">
              {t("Cancel")}
            </div>
          </button>

          <button
            type="submit"
            disabled={isLoading}
            className="cursor-pointer flex py-[14px] px-[16px] gap-[8px] justify-center items-center rounded-[6px] border-[1px] border-solid border-primary bg-primary h-[46px]"
          >
            <Oval
              height={30}
              width={30}
              color="#FFF"
              visible={isLoading}
              ariaLabel="oval-loading"
              secondaryColor="#FFF"
              strokeWidth={5}
              strokeWidthSecondary={3}
            />
            <div className="text-white font-bold text-[16px] not-italic">
              {t("saveChangesCta")}
            </div>
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-[20px] p-[24px] self-stretch">
        <input type="hidden" {...register("isWillingToTravel")} />
        <input type="hidden" {...register("isOpenForSecondment")} />

        {domainsSection.map((_, index) => {
          const selectedDomains = Object.keys(userChosenDomains);
          const domainId = selectedDomains[index];
          const domainOption = domainsOptions.find((d) => d.id === domainId);
          const isOtherDomain = domainOption?.title === domainOtherOption;

          const values = getValues();
          const chosenDomainIds = Object.keys(values).reduce((acc, elem) => {
            if (elem.includes("domainId-") && values[elem]) {
              acc.push(values[elem]);
            }
            return acc;
          }, []);

          return (
            <>
              <div
                key={`domain-${index}`}
                className="flex items-end gap-[20px] self-stretch"
                style={{
                  flex: "1 1",
                }}
              >
                <div className="grid w-full gap-[10px] relative">
                  <Select
                    label={t("Domain")}
                    placeholder={t("placeholderSelect", {
                      field: t("Domain").toLowerCase(),
                    })}
                    name={`domainId-${index}`}
                    containerClassName="flex flex-1 flex-col gap-[10px] "
                    isDisabled={userDetails.flags.lockDomain}
                    value={
                      domainId
                        ? domainsOptions.find((elem) => elem.value === domainId)
                        : null
                    }
                    onChange={(e) => {
                      let data = watch("userDomains") || {};
                      delete data[domainId];

                      setUserChosenDomains((prevState) => {
                        const newData = { ...prevState };
                        delete newData[domainId];
                        return { ...newData, [e.value]: [] };
                      });

                      setValue("userDomains", {
                        ...data,
                        [e.value]: [],
                      });
                      setValue(`domainId-${index}`, e.value);
                      setValue(`subDomains-${index}`, []);

                      if (e?.label === domainOtherOption) {
                        unregister(`customDomain`);
                      }
                    }}
                    closeMenuOnSelect={true}
                    isRequired={true}
                    register={register}
                    registerArgs={{
                      required: t("errorField", { field: t("Domain") }),
                    }}
                    errorMessage={
                      errors[`domainId-${index}`]
                        ? errors[`domainId-${index}`].message
                        : ""
                    }
                    options={domainsOptions.filter(
                      (el) =>
                        !chosenDomainIds.includes(el.value) ||
                        el.value === domainId
                    )}
                  />

                  {userDetails.flags.lockDomain && (
                    <div className="absolute left-[65px] top-[1px]">
                      <LockIcon />
                    </div>
                  )}
                </div>
                <div className="grid w-full gap-[5px] relative">
                  <Select
                    label={t("Subdomain")}
                    placeholder={t("placeholderSelect", {
                      field: t("Subdomain").toLowerCase(),
                    })}
                    name={`subDomains-${index}`}
                    containerClassName="flex flex-1 flex-col gap-[10px] "
                    closeMenuOnSelect={false}
                    isMulti={true}
                    withCheckBox
                    isRequired={true}
                    isDisabled={
                      !domainId ||
                      subDomainsOptions.length === 0 ||
                      userDetails.flags.lockDomain
                    }
                    value={
                      userChosenDomains[domainId] &&
                      userChosenDomains[domainId].length
                        ? subDomainsOptions.filter((elem) =>
                            userChosenDomains[domainId].includes(elem.value)
                          )
                        : null
                    }
                    onChange={(e) => {
                      setUserChosenDomains((prevState) => {
                        return {
                          ...prevState,
                          [domainId]: e.map((item) => item.value),
                        };
                      });
                      let data = watch("userDomains") || {};

                      setValue(
                        `subDomains-${index}`,

                        e.map((item) => item.value)
                      );

                      setValue("userDomains", {
                        ...data,
                        [domainId]: e.map((e) => e.value),
                      });
                    }}
                    register={register}
                    registerArgs={{
                      required: t("errorField", { field: t("Subdomain") }),
                    }}
                    errorMessage={
                      errors[`subDomains-${index}`]
                        ? errors[`subDomains-${index}`].message
                        : ""
                    }
                    options={subDomainsOptions.filter(
                      (el) => el.domainId === domainId
                    )}
                  />

                  {userDetails.flags.lockDomain && (
                    <div className="absolute left-[90px] top-[1px]">
                      <LockIcon />
                    </div>
                  )}
                </div>

                {(index > 0 || domainsSection.length > 1) &&
                  !userDetails.flags.lockDomain && (
                    <div className="flex items-center mt-[30px]">
                      <div
                        className="flex gap-[8px] cursor-pointer"
                        onClick={() => {
                          unregister(`domainId-${index}`);
                          unregister(`subDomains-${index}`);
                          if (isOtherDomain) {
                            unregister("customDomain");
                          }
                          if (
                            values?.userDomains[values[`domainId-${index}`]]
                          ) {
                            delete values.userDomains[
                              values[`domainId-${index}`]
                            ];
                            setValue("userDomains", values.userDomains);
                          }

                          setUserChosenDomains((prevState) => {
                            const newData = { ...prevState };
                            delete newData[domainId];
                            return newData;
                          });
                          const removeDomainSection = domainsSection.filter(
                            (_, i) => i !== index
                          );
                          setDomainSection(removeDomainSection);
                        }}
                      >
                        <TrashIcon />
                      </div>
                    </div>
                  )}
              </div>
              {isOtherDomain && (
                <Input
                  label={t("otherDomain")}
                  placeholder={t("Write domain")}
                  type="text"
                  isRequired={true}
                  name={`customDomain`}
                  errorMessage={
                    errors[`customDomain`] ? errors[`customDomain`].message : ""
                  }
                  register={register}
                  registerArgs={{
                    required: t("errorField", { field: t("others") }),
                    validate: (value) => {
                      return value && value.length > 128
                        ? t("maxCharacters", {
                            field: t("others"),
                            number: 128,
                          })
                        : null;
                    },
                  }}
                  readOnly={userDetails.flags.lockDomain}
                  containerClassName="w-full"
                  labelClassName="text-base-black font-semibold text-[16px]"
                  inputClassName={`flex h-[44px] w-full items-start gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
                    errors[`customDomain`]
                      ? "border-red"
                      : "border-default-color"
                  } ${
                    userDetails.flags.lockDomain
                      ? "bg-[#f2f2f2]"
                      : "bg-transparent"
                  } outline-none text-dark-gray font-regular text-[16px] not-italic placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[12px] px-[14px] resize-none`}
                />
              )}
              {index === domainsSection.length - 1 &&
                domainsSection.length < domainsOptions.length &&
                !userDetails.flags.lockDomain && (
                  <div
                    className="flex gap-[8px] cursor-pointer max-w-[175px]"
                    onClick={() => {
                      addAnotherDomain(index);
                    }}
                  >
                    <AddCircleIcon />
                    <div className="text-primary font-bold text-[16px] not-italic">
                      {t("addAnotherDomain")}
                    </div>
                  </div>
                )}
            </>
          );
        })}

        <div className="grid grid-cols-2 sm-max:grid-cols-1 gap-[20px] self-stretch  mt-[10px]">
          {/* ************** YEARS OF EXPERIENCE ***************/}

          <Input
            label={t("yearsOfExperienceTitle")}
            placeholder={t("placeHolderType", {
              title: t("yearsOfExperienceTitle").toLowerCase(),
            })}
            type="number"
            inputMode="numeric"
            name="yearOfExperience"
            register={register}
            labelClassName="text-primary font-semibold text-[16px]"
            inputClassName="flex h-[44px] w-full  sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid border-default-color bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px] "
          />

          {/* ************** WILLING TO TRAVEL ***************/}
          <RadioButtons
            label={t("willingToTravel")}
            contents={radios}
            containerClassName="flex flex-col flex-1 gap-[16px]"
            radioClassNameContainer="grid grid-cols-2 justify-between w-[250px] sm-max:w-full"
            onClick={(_, index) => {
              setValue("isWillingToTravel", !watch("isWillingToTravel"));

              const updatedContents = radios.map((elem, i) => {
                return { ...elem, isActive: i === index };
              });

              setRadios(updatedContents);
            }}
          />

          <RadioButtons
            label={t("openForSecondment")}
            contents={secondmentRadios}
            containerClassName="flex flex-col flex-1 gap-[16px]"
            radioClassNameContainer="grid grid-cols-2 justify-between w-[250px] sm-max:w-full"
            onClick={(_, index) => {
              setValue(
                "isOpenForSecondment",
                !watch("isOpenForSecondment")
              );

              const updatedContents = secondmentRadios.map((elem, i) => {
                return { ...elem, isActive: i === index };
              });

              setSecondmentRadios(updatedContents);
            }}
          />
        </div>

        <div className="grid grid-cols-2 sm-max:grid-cols-1 gap-[20px] self-stretch mt-[10px]">
          {/* ************** CAT TOOLS ***************/}
          <RadioButtons
            label={t("catToolsExperience")}
            contents={catToolsRadios}
            containerClassName="flex flex-col flex-1 gap-[16px]"
            radioClassNameContainer="grid grid-cols-2 justify-between w-[250px] sm-max:w-full"
            onClick={(_, index) => {
              const newValue = !watch("hasCatToolsExperience");
              setValue("hasCatToolsExperience", newValue);
              if (!newValue) {
                setValue("catTools", []);
              }
              const updatedContents = catToolsRadios.map((elem, i) => {
                return { ...elem, isActive: i === index };
              });
              setCatToolsRadios(updatedContents);
            }}
          />

          {watch("hasCatToolsExperience") && (
            <CreatableMultiSelect
              label={t("catToolsTitle")}
              placeholder={t("catToolsPlaceholder")}
              name="catTools"
              options={catToolsOptions}
              value={
                watch("catTools") && watch("catTools").length > 0
                  ? watch("catTools").map((tool) =>
                      typeof tool === "string"
                        ? { label: tool, value: tool }
                        : tool
                    )
                  : null
              }
              onChange={(selected) => {
                setValue(
                  "catTools",
                  selected.map((item) => item.value)
                );
              }}
              register={register}
              labelClassName="text-primary font-semibold text-[16px]"
            />
          )}
        </div>
      </div>
    </form>
  );
}

EditExperience.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

export default EditExperience;
